// Constants
import { ResponsiveHeaderWidths } from "@ms/planner-generic-controls/lib/local/constants/StyleConstants";
import { ClientFlavor } from "@ms/uno-constants/lib/local/AppConstants";
import { EngagementTelemetryConstants } from "@ms/uno-constants/lib/local/EngagementTelemetryConstants";
import { IrisTriggerAction, IrisUiCommand } from "@ms/uno-constants/lib/local/IrisConstants";
import { ModalType } from "@ms/uno-constants/lib/local/ModalType";
import { AlertUserDebounceInterval } from "@ms/uno-constants/lib/local/NotificationConstants";
import { SubRouteKeys, TryPremiumTabKey, ViewType } from "@ms/uno-constants/lib/local/RoutingConstants";
import { StoreEntityState } from "@ms/uno-constants/lib/local/StoreConstants";
import { ElementIds } from "@ms/uno-constants/lib/local/UIConstants";
import { ProjectUpsellExperienceMode, TryPremiumEntryPoint, UpsellTrialButtonDisplayMode } from "@ms/uno-constants/lib/local/UpsellConstants";
import { ViewName } from "@ms/uno-constants/lib/local/ViewConstants";
// Controls
import { CalendarBundled, CalendarFilled, ChartBundled, ChartFilled, Diamond, DiamondFilled, Gantt, GanttFilled, GoalsBundled, GoalsFilled, PeopleBundled, PeopleFilled, StatusBundled, StatusFilled, TableBundled, TableFilled, TaskBoardBundled, TaskboardFilled } from "@ms/planner-generic-controls/lib/local/icons";
import { GroundingResourcesButton } from "@ms/planner-presentational-controls/lib/local/groundingResourcesButton/GroundingResourcesButton";
import { ViewNavigationBar, ViewTabAppearance } from "@ms/planner-presentational-controls/lib/local/viewNavigationBar/ViewNavigationBar";
// Models
import { GlobalDynamicFilterScope } from "@ms/planner-models/lib/local/client/dynamicFilter/IDynamicFilterScope";
import { EntityFilter } from "@ms/planner-models/lib/local/client/filter/Filter";
import { MAX_DYNAMIC_FILTERS_TOTAL } from "@ms/planner-models/lib/local/constants/DynamicFilterConstants";
import { BasicPlanFilterOptions, DynamicFilterField, FilterTypes, TodoPlanFilterOptions } from "@ms/planner-models/lib/local/constants/FilterConstants";
import { ContainerType } from "@ms/planner-models/lib/local/service/planner/enumerations/IPlannerEnumerations";
// Hooks
import { useActionCreators } from "@ms/planner-container-controls/lib/local/hooks/useActionCreators";
import { useInteractionTracker } from "@ms/planner-presentational-controls/lib/local/hooks/useInteractionTracker";
import { useConfigProvider } from "@ms/planner-container-controls/lib/local/hooks/useConfigProvider";
import { useStores } from "@ms/planner-container-controls/lib/local/hooks/useStores";
import { getPrimaryViewContentWidth } from "@ms/planner-container-controls/lib/local/utilities/TaskEditorPanelLayoutUtilities";
import { useLoggers } from "@ms/planner-presentational-controls/lib/local/utilities/hooks/useLoggers";
// Stores
import { Store } from "@ms/planner-stores/lib/local/stores/Store";
// Action Creators
import { ActionCreator } from "@ms/planner-actioncreators/lib/local/actioncreators/ActionCreator";
// Utilities
import { useAnnounce } from "@fluentui/react-components";
import { format } from "@fluentui/utilities";
import { useDebouncedCallback } from "@ms/planner-generic-controls/lib/local/hooks/useDebouncedCallback";
import { getCustomFieldDefinitionId, isCustomFieldColumn } from "@ms/planner-models/lib/local/utilities/CustomFieldDefinitionUtilities";
import { buildLabelsFromPlanCategories, resolveColumnLabel } from "@ms/planner-presentational-controls/lib/local/utilities/LabelUtilities";
import { getContainerMembersFromStores } from "@ms/planner-stores/lib/local/utilities/ContainerUtilities";
import { canPlanAccessGoals, getContainerPermissionsFromStores } from "@ms/planner-stores/lib/local/utilities/PermissionsUtilities";
import { TaskGroupingOption } from "@ms/uno-constants/lib/local/GroupingConstants";
import { Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
import * as React from "react";
// Constants for this component
const componentName = "ViewNavigationBarContainer";
/**
 * Where in the plan the filter was applied, so one interaction name can still be read per view.
 * Qualified with the page because the same view types also exist under My Tasks.
 * Null for views with no filter menu, which are not measured.
 */ const getFilterInteractionEntryPoint = (viewType)=>{
    switch(viewType){
        case ViewType.Board:
            return "planBoard";
        case ViewType.Schedule:
            return "planCalendar";
        case ViewType.Charts:
            return "planCharts";
        case ViewType.Grid:
            return "planGrid";
        case ViewType.Timeline:
            return "planTimeline";
        case ViewType.People:
            return "planPeople";
        case ViewType.Goals:
            return "planGoals";
        default:
            return null;
    }
};
/**
 * Smart component for plan command navigation bar
 */ export const ViewNavigationBarContainer = ({ planId })=>{
    const [planStore, routeStore, viewStore, capabilitiesStore, upsellStore, userStore, bucketStore, goalStore, permissionsStore, modernGroupsStore, appContextStore, rosterStore, teamsChannelStore, driveItemStore, todoStore, taskStore, customFieldDefinitionStore, modalStore, dynamicFilterStore, dependencyStore] = useStores([
        Store.Plan,
        Store.Route,
        Store.View,
        Store.Capabilities,
        Store.Upsell,
        Store.User,
        Store.Bucket,
        Store.Goal,
        Store.Permissions,
        Store.ModernGroups,
        Store.AppContext,
        Store.Roster,
        Store.TeamsChannel,
        Store.DriveItem,
        Store.Todo,
        Store.Tasks,
        Store.CustomFieldDefinition,
        Store.Modal,
        Store.DynamicFilter,
        Store.Dependency
    ]);
    const [routeActionCreator, userActionCreator, viewTransformsActionCreator, upsellActionCreator, modalActionCreator, copilotActionCreator, dynamicFilterActionCreator, campaignActionCreator] = useActionCreators([
        ActionCreator.Routing,
        ActionCreator.User,
        ActionCreator.ViewTransforms,
        ActionCreator.Upsell,
        ActionCreator.Modal,
        ActionCreator.Copilot,
        ActionCreator.DynamicFilter,
        ActionCreator.Campaign
    ]);
    // Latency measurement
    const { startInteraction } = useInteractionTracker();
    const configProvider = useConfigProvider();
    const primaryViewWidth = getPrimaryViewContentWidth({
        viewStore,
        modalStore,
        taskStore,
        planStore,
        permissionsStore
    }, configProvider);
    const { engagementLogger, traceLogger } = useLoggers();
    const currentPlan = planStore.getPlan(planId).data;
    const labels = React.useMemo(()=>buildLabelsFromPlanCategories(currentPlan), [
        currentPlan
    ]);
    // Dispatch campaign triggers only once an Iris anchor element is present in the DOM, so a creative never activates
    // without its anchor. Each trigger is dispatched at most once per component lifetime to avoid redundant dispatches.
    const hasDispatchedDynamicFilterAppliedRef = React.useRef(false);
    const hasDispatchedFilterMenuOpenedRef = React.useRef(false);
    const [isFilterMenuOpen, setIsFilterMenuOpen] = React.useState(false);
    const viewId = React.useMemo(()=>({
            name: ViewName.PlanPage,
            partitionKey: planId
        }), [
        planId
    ]);
    // Check if a filter is active
    const currentTaskFilter = viewStore.getCurrentTaskFilter(viewId) ?? new EntityFilter();
    const filterKeyword = viewStore.getFilterKeyword(viewId);
    const filterCount = currentTaskFilter.filterCount;
    // Announce filter results to screen readers. When the result-count helper text flight is on it renders a
    // polite live region, so this imperative announcement only runs with the flight off to preserve the old experience.
    const { announce } = useAnnounce();
    const prevFilterStateRef = React.useRef(null);
    const announceCallback = React.useCallback((message)=>{
        announce(message, {
            polite: true
        });
    }, [
        announce
    ]);
    const debouncedAnnounce = useDebouncedCallback(announceCallback, AlertUserDebounceInterval);
    React.useEffect(()=>{
        const prevState = prevFilterStateRef.current;
        const currentState = {
            filterCount,
            keyword: filterKeyword
        };
        const hasFilterChanged = prevState !== null && (prevState.filterCount !== currentState.filterCount || prevState.keyword !== currentState.keyword);
        if (hasFilterChanged && !configProvider().flights.EnableTaskFilterResultCount) {
            const filteredTaskCount = taskStore.getFilteredTaskCount(viewId);
            if (filteredTaskCount === 0) {
                debouncedAnnounce(Strings.TaskFiltering_FilterNoResultsFound);
            } else {
                debouncedAnnounce(format(Strings.TaskFiltering_FilterResultsCountAnnouncement, filteredTaskCount));
            }
        }
        prevFilterStateRef.current = currentState;
    }, [
        filterCount,
        filterKeyword,
        taskStore,
        viewId,
        debouncedAnnounce,
        configProvider
    ]);
    const containerUtilitiesStores = {
        userStore,
        modernGroupsStore,
        rosterStore,
        teamsChannelStore,
        todoStore,
        appContextStore,
        driveItemStore,
        planStore
    };
    const permissionsUtilitiesStores = {
        ...containerUtilitiesStores,
        permissionsStore,
        dependencyStore,
        taskStore
    };
    const containerType = currentPlan?.container?.containerType;
    const isTodoContainer = containerType === ContainerType.ToDo;
    const currentPivot = routeStore.getCurrentViewType();
    const enableFiltersInGoalsView = configProvider().flights.EnableFiltersInGoalsView;
    const shouldShowTaskFilter = currentPivot !== ViewType.Report && (currentPivot !== ViewType.Goals || enableFiltersInGoalsView);
    const shouldShowKeywordFilter = currentPivot !== ViewType.Report && (currentPivot !== ViewType.Goals || enableFiltersInGoalsView);
    const shouldShowViewTabs = containerType !== ContainerType.ToDo;
    // Dynamic filter pill: gated by EnablePlannerAgentFiltering.
    // AI filters remain hidden in Reports and in Goals unless EnableFiltersInGoalsView is enabled.
    // Existing agent-filter and persistence flights still apply.
    const isSmartFilterEnabled = configProvider().flights.EnablePlannerAgentFiltering && shouldShowTaskFilter;
    const isPersistenceEnabled = isSmartFilterEnabled && !isTodoContainer && configProvider().flights.EnableDynamicFilterPersistence;
    const dynamicFilter = configProvider().flights.EnablePlannerAgentFiltering && shouldShowTaskFilter ? currentTaskFilter?.filters?.find((f)=>f.field === DynamicFilterField && f.label) ?? null : null;
    const filterScope = GlobalDynamicFilterScope;
    const savedFilters = isPersistenceEnabled ? dynamicFilterStore.getDynamicFiltersForScope(filterScope) : [];
    const selectedFilterId = isPersistenceEnabled ? viewStore.getSelectedDynamicFilterId(viewId) : null;
    const selectedDynamicFilter = selectedFilterId ? dynamicFilterStore.getDynamicFilter(selectedFilterId) : null;
    const appliedFilterId = selectedDynamicFilter?.id;
    const isDynamicFilterSaveAtCap = isPersistenceEnabled && dynamicFilterStore.getTotalDynamicFilterCount() >= MAX_DYNAMIC_FILTERS_TOTAL;
    // The "more options" anchor renders when a labeled dynamic filter is applied but not yet saved; only then should we
    // dispatch the DynamicFilterApplied trigger.
    const shouldDispatchIrisDynamicFilterApplied = isPersistenceEnabled && dynamicFilter?.label != null && !appliedFilterId;
    // Re-check for the anchor whenever the pill's render condition flips to true, then confirm it is in the DOM.
    React.useEffect(()=>{
        if (hasDispatchedDynamicFilterAppliedRef.current || !shouldDispatchIrisDynamicFilterApplied) {
            return;
        }
        if (document.getElementById(ElementIds.DynamicFilterPillMoreOptions) != null) {
            hasDispatchedDynamicFilterAppliedRef.current = true;
            campaignActionCreator.dispatchCampaignTriggerActions([
                IrisTriggerAction.DynamicFilterApplied
            ]);
        }
    }, [
        shouldDispatchIrisDynamicFilterApplied,
        campaignActionCreator
    ]);
    // The "Create" entry (FilterMenuOpened anchor) renders only while the filter menu is open in its empty state:
    // persistence on, no labeled dynamic filter applied, and no saved filters. Only then should we dispatch the trigger.
    const shouldDispatchIrisFilterMenuOpened = isFilterMenuOpen && isPersistenceEnabled && dynamicFilter?.label == null && savedFilters.length === 0;
    // Re-check for the anchor whenever the Create entry's render condition flips to true, then confirm it is in the DOM.
    React.useEffect(()=>{
        if (hasDispatchedFilterMenuOpenedRef.current || !shouldDispatchIrisFilterMenuOpened) {
            return;
        }
        if (document.getElementById(ElementIds.SmartFilterCreateButton) != null) {
            hasDispatchedFilterMenuOpenedRef.current = true;
            campaignActionCreator.dispatchCampaignTriggerActions([
                IrisTriggerAction.FilterMenuOpened
            ]);
        }
    }, [
        shouldDispatchIrisFilterMenuOpened,
        campaignActionCreator
    ]);
    // Run a pending imperative UI command (e.g. from an Iris campaign CTA), then clear it.
    const pendingUiCommand = viewStore.getPendingUiCommand();
    React.useEffect(()=>{
        if (pendingUiCommand === IrisUiCommand.OpenFilterMenu) {
            setIsFilterMenuOpen(true);
            viewTransformsActionCreator.requestUiCommand(null);
        }
    }, [
        pendingUiCommand,
        viewTransformsActionCreator
    ]);
    // Defensively clear any unconsumed UI command on unmount so it can't leak into a different plan's view.
    React.useEffect(()=>{
        return ()=>{
            viewTransformsActionCreator.requestUiCommand(null);
        };
    }, [
        viewTransformsActionCreator
    ]);
    if (!currentPlan) {
        return /*#__PURE__*/ React.createElement(React.Fragment, null);
    }
    // Pre-compute flight values to determine goal and custom fields filter availability
    const { EnableGoalsInPlan, EnableGoalsInBoardView, EnableCustomFields, EnableTimelineViewEvo } = configProvider().flights;
    const isGoalFilterEnabled = EnableGoalsInPlan && EnableGoalsInBoardView;
    // FinishDate filter is gated by the same flight that gates the FinishDate column
    // (EnableTimelineViewEvo). This keeps the filter visible only when the feature is on.
    const filterOptionsForUnifiedPlan = BasicPlanFilterOptions.filter((option)=>{
        if (option === FilterTypes.GoalIds) return isGoalFilterEnabled;
        if (option === FilterTypes.FinishDateTime) return EnableTimelineViewEvo;
        return true;
    });
    // Compute task filter options based on container type and feature flags
    const taskFilterOptions = isTodoContainer ? TodoPlanFilterOptions : filterOptionsForUnifiedPlan;
    // Get goals data only when goals can be accessed
    const goalsForFilter = isGoalFilterEnabled ? goalStore.getGoalsForPlan(planId).data : undefined;
    const isTabView = configProvider().sessionMetaData.appMetadata.clientFlavor === ClientFlavor.Tab;
    /**
     * Callback for when the keyword filter input value changes
     * @param filterInput The new filter input value
     */ const onUpdateFilterInputValue = (filterInput)=>{
        engagementLogger.logEngagement(componentName, EngagementTelemetryConstants.FilterByKeyword);
        viewTransformsActionCreator.setTaskFilterKeyword(viewId, filterInput);
    };
    /**
     * Determine whether the user should see upsell pivot hooks
     */ const getShowUpsellHooks = ()=>{
        const currentProjectUpsellExperienceMode = upsellStore.getUpsellExperienceMode();
        const plannerPremiumDetails = capabilitiesStore.getIsPlannerPremiumEnabled();
        const config = configProvider();
        const isPlannerPremiumEnabled = plannerPremiumDetails.data && config.settings.EnvironmentFeatureSettings.EnablePremiumPlanExperience;
        return !!isPlannerPremiumEnabled && config.flights.EnableUpsellExperienceEvo && currentProjectUpsellExperienceMode !== ProjectUpsellExperienceMode.Disabled && currentProjectUpsellExperienceMode !== ProjectUpsellExperienceMode.TrialExpired && currentProjectUpsellExperienceMode !== ProjectUpsellExperienceMode.OfficeSubscriptionNeeded && currentProjectUpsellExperienceMode !== ProjectUpsellExperienceMode.TrialConversion;
    };
    const showUpsellHooks = getShowUpsellHooks();
    /**
     * Callback to trigger when a pivot is changed.
     * Triggers upsell modal for premium features (Timeline, People, Reports, Goals) if upsell hooks are enabled.
     * Also handles "try-premium" button click.
     * @param pivotKey The new pivot key
     */ const onPivotChange = (pivotKey)=>{
        engagementLogger.logEngagement(componentName, EngagementTelemetryConstants.SwitchView, {
            viewType: pivotKey,
            planId
        });
        // Timeline is only a premium feature when the flight is disabled
        const isPremiumFeature = !canAccessTimeline() && pivotKey === ViewType.Timeline || pivotKey === ViewType.People || pivotKey === ViewType.Assignments || pivotKey === TryPremiumTabKey;
        if (showUpsellHooks && isPremiumFeature) {
            if (pivotKey === TryPremiumTabKey) {
                upsellActionCreator.openUpsellModalForUser(TryPremiumEntryPoint.PremiumPlanHeader);
            } else if (pivotKey === ViewType.Timeline) {
                upsellActionCreator.openUpsellModalForUser(TryPremiumEntryPoint.PremiumPlanOverflowTimeline);
            }
        // Other premium pivots (People, Reports) aren't clickable
        } else {
            // Close the task editor if it's the topmost modal before switching views
            if (modalStore.getCurrentModalData().modalType === ModalType.TaskEditor) {
                modalActionCreator.closeModal();
            }
            routeActionCreator.replaceSubRoute([
                planId,
                SubRouteKeys.View,
                pivotKey
            ]);
        }
    };
    /**
     * Helper to determine if user/container can access goals
     */ const canAccessGoals = ()=>{
        const container = currentPlan.container;
        const containerPermissions = getContainerPermissionsFromStores(container, permissionsUtilitiesStores, traceLogger);
        // Check if the user has copilot access and if the container supports FAB and goals in basic plan
        const userCanAccessGoals = permissionsStore.getUserPermissions().canAccessGoals;
        return !!(containerType !== ContainerType.ToDo && canPlanAccessGoals(currentPlan, userCanAccessGoals, configProvider, containerPermissions.canUseGoals));
    };
    // Reports view is only accessible in user and group containers when the flight is enabled
    const canAccessReports = ()=>{
        if (!configProvider().flights.EnableStatusReportsForEvoPlans) {
            return false;
        }
        const container = currentPlan.container;
        const canUseCopilot = permissionsStore.getUserPermissions().canUseCopilot;
        return canUseCopilot && (container.containerType === ContainerType.User || container.containerType === ContainerType.Group);
    };
    // Timeline view is only accessible when the flight is enabled and user has premium access
    const canAccessTimeline = ()=>{
        return permissionsStore.getUserPermissions().canAccessTimelineView;
    };
    // Team View is only accessible when the EnableTeamViewEvo flight is enabled and the user has premium access
    const canAccessTeamView = ()=>{
        return permissionsStore.getUserPermissions().canAccessTeamView;
    };
    // Method to build the list of tabs that are always visible
    const getVisiblePageTabItems = (showUpsellHooks)=>{
        const tabs = [];
        // Goals - show as first pill if user can access it
        if (canAccessGoals()) {
            tabs.push({
                headerText: Strings.SharedComponents_PlanGoalsPageName,
                itemIcon: /*#__PURE__*/ React.createElement(GoalsBundled, null),
                itemIconFilled: /*#__PURE__*/ React.createElement(GoalsFilled, null),
                itemKey: ViewType.Goals,
                tooltipText: Strings.SharedComponents_PlanGoalsTooltip,
                tabElementId: ElementIds.GoalsViewPivot
            });
        }
        tabs.push({
            headerText: Strings.SharedComponents_PlanGridPageName,
            itemIcon: /*#__PURE__*/ React.createElement(TableBundled, null),
            itemIconFilled: /*#__PURE__*/ React.createElement(TableFilled, null),
            itemKey: ViewType.Grid,
            tooltipText: Strings.SharedComponents_PlanGridTooltip,
            tabElementId: ElementIds.GridViewPivot
        }, {
            headerText: Strings.SharedComponents_PlanTaskboardPageName,
            itemIcon: /*#__PURE__*/ React.createElement(TaskBoardBundled, null),
            itemIconFilled: /*#__PURE__*/ React.createElement(TaskboardFilled, null),
            itemKey: ViewType.Board,
            tooltipText: Strings.SharedComponents_PlanTaskboardTooltip,
            tabElementId: ElementIds.BoardViewPivot
        });
        // Timeline - only show if upsell hooks are enabled or user has access
        if (showUpsellHooks || canAccessTimeline()) {
            tabs.push({
                headerText: Strings.SharedComponents_PlanTimelinePageName,
                itemIcon: canAccessTimeline() ? /*#__PURE__*/ React.createElement(Gantt, null) : /*#__PURE__*/ React.createElement(Diamond, null),
                itemIconFilled: canAccessTimeline() ? /*#__PURE__*/ React.createElement(GanttFilled, null) : /*#__PURE__*/ React.createElement(DiamondFilled, null),
                itemKey: ViewType.Timeline,
                tooltipText: Strings.SharedComponents_PlanTimelineTooltip,
                tabElementId: ElementIds.TimelineViewPivot
            });
        }
        // Team — shown only when the user has Team access. canAccessTeamView already encodes both the
        // EnableTeamViewEvo flight and the P3+ licence check, so no separate flight check is needed here.
        // There is no upsell hook: users without access simply don't see the tab. People remains a
        // separate upsell surface in the overflow menu below.
        if (canAccessTeamView()) {
            tabs.push({
                headerText: Strings.SharedComponents_PlanTeamViewPageName,
                itemIcon: /*#__PURE__*/ React.createElement(PeopleBundled, null),
                itemIconFilled: /*#__PURE__*/ React.createElement(PeopleFilled, null),
                itemKey: ViewType.People,
                tooltipText: Strings.SharedComponents_PlanTeamViewTooltip,
                tabElementId: ElementIds.PeopleViewPivot
            });
        }
        tabs.push({
            headerText: Strings.SharedComponents_PlanCalendarPageName,
            itemIcon: /*#__PURE__*/ React.createElement(CalendarBundled, null),
            itemIconFilled: /*#__PURE__*/ React.createElement(CalendarFilled, null),
            itemKey: ViewType.Schedule,
            tooltipText: Strings.SharedComponents_PlanCalendarTooltip,
            tabElementId: ElementIds.ScheduleViewPivot
        });
        if (canAccessReports()) {
            tabs.push({
                headerText: Strings.SharedComponents_PlanReportsPageName,
                itemIcon: /*#__PURE__*/ React.createElement(StatusBundled, null),
                itemIconFilled: /*#__PURE__*/ React.createElement(StatusFilled, null),
                itemKey: ViewType.Report,
                tooltipText: Strings.SharedComponents_PlanReportsTooltip,
                tabElementId: ElementIds.StatusReportViewPivotEvo
            });
        }
        tabs.push({
            headerText: Strings.SharedComponents_PlanChartsPageName,
            itemIcon: /*#__PURE__*/ React.createElement(ChartBundled, null),
            itemIconFilled: /*#__PURE__*/ React.createElement(ChartFilled, null),
            itemKey: ViewType.Charts,
            tooltipText: Strings.SharedComponents_PlanChartsTooltip,
            tabElementId: ElementIds.ChartsViewPivot
        });
        return tabs;
    };
    const visiblePageTabItems = getVisiblePageTabItems(showUpsellHooks);
    // Method to build the list of tabs to be shown in the overflow menu
    const getOverflowPageTabItems = (showUpsellHooks)=>{
        const tabs = [];
        // Overflow tabs (only when upsell is enabled): People, Goals, "Try premium"
        if (showUpsellHooks) {
            const displayMode = upsellStore.getUpsellTrialButtonDisplayMode();
            const tryPremiumButtonText = displayMode === UpsellTrialButtonDisplayMode.RequestSubscription ? Strings.UpsellStatus_AskForLicenseButtonText : Strings.SharedComponents_PlanTryPremiumButtonLabel;
            const tryPremiumTooltipText = displayMode === UpsellTrialButtonDisplayMode.RequestSubscription ? Strings.SharedComponents_PlanAskForLicenseTooltip : Strings.SharedComponents_PlanTryPremiumTooltip;
            // People is its own upsell surface (kept separate from Team). Always offer the suppressed
            // overflow entry while upsell hooks are on, regardless of the Team flight.
            tabs.push({
                headerText: Strings.SharedComponents_PlanPeoplePageName,
                itemKey: ViewType.People,
                tooltipText: Strings.SharedComponents_PlanPeopleTooltip,
                tabElementId: ElementIds.PeopleViewPivot,
                itemIcon: /*#__PURE__*/ React.createElement(PeopleBundled, null),
                itemIconFilled: /*#__PURE__*/ React.createElement(PeopleFilled, null),
                appearance: ViewTabAppearance.Suppressed,
                isHidden: true
            });
            if (configProvider().flights.EnableGoalsInPlan) {
                tabs.push({
                    headerText: Strings.SharedComponents_PlanGoalsPageName,
                    itemKey: ViewType.Goals,
                    tooltipText: Strings.SharedComponents_PlanGoalsTooltip,
                    tabElementId: ElementIds.GoalsViewPivot,
                    itemIcon: /*#__PURE__*/ React.createElement(GoalsBundled, null),
                    itemIconFilled: /*#__PURE__*/ React.createElement(GoalsFilled, null),
                    appearance: ViewTabAppearance.Suppressed,
                    isHidden: true
                });
            }
            tabs.push({
                headerText: tryPremiumButtonText,
                itemKey: TryPremiumTabKey,
                tabElementId: TryPremiumTabKey,
                tooltipText: tryPremiumTooltipText,
                itemIcon: /*#__PURE__*/ React.createElement(Diamond, null),
                itemIconFilled: /*#__PURE__*/ React.createElement(DiamondFilled, null),
                appearance: ViewTabAppearance.Emphasized,
                isHidden: true
            });
        }
        return tabs;
    };
    const overflowPageTabItems = getOverflowPageTabItems(showUpsellHooks);
    // TODO: 3110448 Refactor this code and move it to an AC
    const handInPrompt = (promptText, isDraft)=>{
        const promptContext = {
            promptText,
            isDraft
        };
        const copilotSidecarState = viewStore.getCopilotSidecarState();
        if (copilotSidecarState.open && copilotSidecarState.collapsed) {
            viewTransformsActionCreator.setCopilotSidecarState({
                open: true
            });
            copilotActionCreator.handIn(promptContext);
        } else if (copilotSidecarState.open) {
            copilotActionCreator.handIn(promptContext);
        } else {
            viewTransformsActionCreator.setCopilotSidecarState({
                open: true,
                initialHandInContext: promptContext
            });
        }
    };
    const handleSmartFilterClick = ()=>{
        engagementLogger.logEngagement(EngagementTelemetryConstants.PlannerCopilot, EngagementTelemetryConstants.SmartFilterEntryPointClicked, {
            planId,
            viewType: currentPivot ?? ""
        });
        handInPrompt(Strings.CopilotDab_SuggestionTopTasks, true);
    };
    const handleSmartFilterSaveClick = dynamicFilter?.label ? ()=>{
        engagementLogger.logEngagement(componentName, EngagementTelemetryConstants.DynamicFilterSaved);
        modalActionCreator.openModal({
            modalType: ModalType.SaveDynamicFilter,
            currentTitle: dynamicFilter.label,
            currentDescription: dynamicFilter.label,
            scope: filterScope,
            viewId
        });
    } : undefined;
    const handleEditFilter = (filterId)=>{
        const filter = dynamicFilterStore.getDynamicFilter(filterId);
        if (!filter) {
            return;
        }
        engagementLogger.logEngagement(componentName, EngagementTelemetryConstants.SavedDynamicFilterEdited);
        modalActionCreator.openModal({
            modalType: ModalType.EditDynamicFilter,
            filterId: filter.id,
            currentTitle: filter.title,
            currentDescription: filter.description
        });
    };
    const handleSelectFilter = (filterId)=>{
        // Clicking the already-applied saved filter toggles it off instead of re-sending the prompt.
        if (filterId === appliedFilterId) {
            engagementLogger.logEngagement(componentName, EngagementTelemetryConstants.DynamicFilterRemoved);
            viewTransformsActionCreator.removeDynamicFilter(viewId, true);
            return;
        }
        const filter = dynamicFilterStore.getDynamicFilter(filterId);
        if (!filter) {
            return;
        }
        engagementLogger.logEngagement(componentName, EngagementTelemetryConstants.SavedDynamicFilterApplied);
        handInPrompt(filter.description, false);
    };
    const handleDeleteFilter = (filterId)=>{
        engagementLogger.logEngagement(componentName, EngagementTelemetryConstants.SavedDynamicFilterDeleted);
        dynamicFilterActionCreator.deleteDynamicFilter(filterId, viewId);
    };
    const smartFilterProps = isPersistenceEnabled ? {
        onSmartFilterClick: handleSmartFilterClick,
        onSmartFilterSaveClick: handleSmartFilterSaveClick,
        isSaveAtCap: isDynamicFilterSaveAtCap,
        savedFilters,
        appliedFilterId,
        onEditFilter: handleEditFilter,
        onDeleteFilter: handleDeleteFilter,
        onSelectFilter: handleSelectFilter
    } : undefined;
    // Compute helper text for keyword filter results
    const getKeywordFilterHelperText = ()=>{
        if (!configProvider().flights.EnableTaskFilterResultCount) {
            return undefined;
        }
        if (filterKeyword.trim().length === 0 && filterCount === 0) {
            return undefined;
        }
        const tasksForPlanResult = taskStore.getTasksForPlan(planId);
        if (tasksForPlanResult.status.state !== StoreEntityState.Present) {
            return undefined;
        }
        const filteredTaskCount = taskStore.getFilteredTaskCount(viewId);
        if (filteredTaskCount === 0) {
            return Strings.TaskFiltering_FilterNoResultsFound;
        }
        const resultCountString = filteredTaskCount === 1 ? Strings.TaskFiltering_FilterResultOfTotal : Strings.TaskFiltering_FilterResultsOfTotal;
        return format(resultCountString, filteredTaskCount, tasksForPlanResult.data.length);
    };
    const openGroundingResourcesDialog = ()=>{
        if (!planId) return;
        viewTransformsActionCreator.setFABState({
            mode: "collapsed"
        });
        modalActionCreator.openModal({
            modalType: ModalType.GroundingResources,
            planId,
            sourceComponentName: componentName
        });
    };
    const isKnowledgeSourceButtonEnabled = currentPivot === ViewType.Board && viewStore.getCurrentTaskGroupingState(viewId) === TaskGroupingOption.ProjectManagerWorkflowState;
    const gridSortCriteria = currentPivot === ViewType.Grid ? viewStore.getSortCriteria(viewId) : null;
    let sortPillProps;
    if (gridSortCriteria) {
        const colId = gridSortCriteria.primary.field;
        const staticLabel = resolveColumnLabel(colId);
        const columnLabel = staticLabel !== colId ? staticLabel : customFieldDefinitionStore.getCustomFieldDefinition(isCustomFieldColumn(colId) ? getCustomFieldDefinitionId(colId) : colId).data?.displayName ?? colId;
        sortPillProps = {
            columnLabel,
            onClearSort: ()=>viewTransformsActionCreator.setSortCriteria(viewId, null)
        };
    }
    return /*#__PURE__*/ React.createElement(ViewNavigationBar, {
        viewTabsProps: shouldShowViewTabs ? {
            itemProps: [
                // All visible tabs appear normally
                ...visiblePageTabItems,
                // Overflow tabs appear as upsell hooks
                ...overflowPageTabItems
            ],
            selectedTabKey: currentPivot,
            onSelectTabItem: onPivotChange,
            overflowButtonAriaLabel: Strings.SharedComponents_PlanMoreViewsAriaLabel,
            overflowButtonTooltip: Strings.SharedComponents_PlanMoreViewsAriaLabel
        } : undefined,
        keywordFilterProps: shouldShowKeywordFilter ? {
            key: planId,
            placeholder: Strings.TaskFiltering_KeywordFilterInputPlaceholderText,
            filterInput: viewStore.getFilterKeyword(viewId),
            onFilterInputUpdated: onUpdateFilterInputValue,
            helperText: getKeywordFilterHelperText()
        } : undefined,
        dynamicFilterPillProps: dynamicFilter?.label ? {
            filterLabel: selectedDynamicFilter?.title ?? dynamicFilter.label,
            isActive: dynamicFilter.active,
            isSaved: !!appliedFilterId,
            onToggle: ()=>{
                engagementLogger.logEngagement(componentName, EngagementTelemetryConstants.DynamicFilterToggled, {
                    isActive: String(!dynamicFilter.active)
                });
                viewTransformsActionCreator.setTaskFilter(viewId, currentTaskFilter.setFilterActive(DynamicFilterField, !dynamicFilter.active));
            },
            onDismiss: ()=>{
                engagementLogger.logEngagement(componentName, EngagementTelemetryConstants.DynamicFilterRemoved);
                viewTransformsActionCreator.removeDynamicFilter(viewId, true);
            },
            onSave: !appliedFilterId && isPersistenceEnabled ? handleSmartFilterSaveClick : undefined,
            onEdit: appliedFilterId ? ()=>handleEditFilter(appliedFilterId) : undefined,
            onDelete: appliedFilterId ? ()=>handleDeleteFilter(appliedFilterId) : undefined
        } : undefined,
        sortPillProps: sortPillProps,
        taskFilterProps: shouldShowTaskFilter ? {
            currentTaskFilter,
            getUserPhoto: (userId)=>userStore.getUserPhoto(userId).data,
            onRenderPersona: (user)=>userActionCreator.fetchUsers([
                    user.id
                ]),
            setTaskFilter: (currentTaskFilter, isClearFilters, filterRemoved, announcementTitleStrParams)=>{
                const entryPoint = getFilterInteractionEntryPoint(currentPivot);
                if (entryPoint != null) {
                    startInteraction({
                        name: "FilterByMenu",
                        key: isClearFilters ? null : currentTaskFilter
                    }, viewId, entryPoint);
                }
                viewTransformsActionCreator.setTaskFilter(viewId, currentTaskFilter, isClearFilters, filterRemoved, announcementTitleStrParams);
            },
            taskFilterOptions,
            buckets: bucketStore.getBucketsForPlan(planId).data,
            labels: labels,
            goals: goalsForFilter,
            customFieldDefinitions: EnableCustomFields ? customFieldDefinitionStore.getCustomFieldDefinitionsForPlan(planId).data : undefined,
            members: getContainerMembersFromStores(currentPlan.container, containerUtilitiesStores),
            isTabView,
            smartFilterProps,
            open: isFilterMenuOpen,
            onMenuOpenChange: setIsFilterMenuOpen,
            enableBlockedStatus: configProvider().flights.EnableBlockedStatus
        } : undefined,
        width: primaryViewWidth,
        secondaryActionButton: isKnowledgeSourceButtonEnabled ? /*#__PURE__*/ React.createElement(GroundingResourcesButton, {
            onClick: openGroundingResourcesDialog,
            showLabel: primaryViewWidth >= ResponsiveHeaderWidths.maxWidth1536
        }) : undefined
    });
};
